import React from "react";
import { Card, Button } from "react-bootstrap";
import { FiFilter as Filter } from "react-icons/fi";
import { ALERT_TYPE_MAPPING, ALERT_COLORS } from "../../General/alertTypes";

const FILTER_STYLES = {
  sectionHeader: { fontSize: "14px", textAlign: "left" },
  toggleButton: { fontSize: "13px" },
  scrollableList: { maxHeight: "200px", overflowY: "auto", textAlign: "left" },
  searchInput: { fontSize: "14px", textAlign: "left", padding: "8px 12px" },
  formLabel: {
    fontSize: "14px",
    textAlign: "left",
    fontWeight: "500",
    marginLeft: "8px",
  },
};

export default function FilterPanel({
  // Filter data
  selectedObjects,
  selectedLines,
  selectedRecipes,
  selectedAlertTypes,
  uniqueObjects,
  uniqueLines,
  uniqueRecipes,
  uniqueAlertTypes,
  filteredObjects,
  filteredRecipes,

  // Setters (from useCipFilters hook)
  setSelectedObjects,
  setSelectedLines,
  setSelectedRecipes,
  setSelectedAlertTypes,

  // Search
  searchFilters,
  updateSearchFilter,

  // Functions
  clearAllFilters,
}) {
  const sortedAlertTypes = uniqueAlertTypes.sort((a, b) => {
    if (String(a) === "80") return 1;
    if (String(b) === "80") return -1;
    return Number(a) - Number(b);
  });

  // Toggle handlers
  const handleAlertTypeToggle = () => {
    // Filter out alert type 80 (Unclassified) for select all
    const selectableAlertTypes = uniqueAlertTypes.filter(
      (alertType) => String(alertType) !== "80"
    );

    if (selectedAlertTypes.length === selectableAlertTypes.length) {
      // If all selectable alerts are selected, deselect all
      setSelectedAlertTypes([]);
    } else {
      // Select all except Unclassified (80)
      setSelectedAlertTypes([...selectableAlertTypes]);
    }
  };

  const handleLinesToggle = () => {
    if (selectedLines.length === uniqueLines.length) {
      setSelectedLines([]);
    } else {
      setSelectedLines([...uniqueLines]);
    }
  };

  const handleRecipesToggle = () => {
    if (selectedRecipes.length === uniqueRecipes.length) {
      setSelectedRecipes([]);
    } else {
      setSelectedRecipes([...uniqueRecipes]);
    }
  };

  const handleObjectsToggle = () => {
    if (selectedObjects.length === uniqueObjects.length) {
      setSelectedObjects([]);
    } else {
      setSelectedObjects([...uniqueObjects]);
    }
  };

  // Multi-select handler
  const handleMultiSelect = (item, selectedItems, setSelectedItems) => {
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter((selected) => selected !== item));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const activeFilterCount =
    uniqueAlertTypes.length -
    selectedAlertTypes.length +
    (uniqueLines.length - selectedLines.length) +
    (uniqueRecipes.length - selectedRecipes.length) +
    (uniqueObjects.length - selectedObjects.length);

  return (
    <Card className="h-100">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0">
            <Filter size={16} className="mr-2" />
            Filters
          </h6>
          {activeFilterCount > 0 && (
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={clearAllFilters}
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Alert Types */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div
              className="text-muted font-weight-bold"
              style={FILTER_STYLES.sectionHeader}
            >
              ALERT TYPES
            </div>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-primary font-weight-medium"
              style={FILTER_STYLES.toggleButton}
              onClick={handleAlertTypeToggle}
            >
              {(() => {
                const selectableAlertTypes = uniqueAlertTypes.filter(
                  (alertType) => String(alertType) !== "80"
                );
                return selectedAlertTypes.length === selectableAlertTypes.length
                  ? "Deselect All"
                  : "Select All";
              })()}
            </Button>
          </div>
          <div style={FILTER_STYLES.scrollableList}>
            {sortedAlertTypes.map((alertType) => (
              <div
                key={alertType}
                className="d-flex align-items-center py-2 cursor-pointer"
                onClick={() =>
                  handleMultiSelect(
                    alertType,
                    selectedAlertTypes,
                    setSelectedAlertTypes
                  )
                }
                style={{ textAlign: "left" }}
              >
                <div
                  className="rounded mr-3"
                  style={{
                    width: "14px",
                    height: "14px",
                    backgroundColor: selectedAlertTypes.includes(alertType)
                      ? ALERT_COLORS[alertType] || "#64748b"
                      : "#e9ecef",
                  }}
                />
                <div className="flex-grow-1" style={{ textAlign: "left" }}>
                  <div
                    style={{
                      fontSize: "15px",
                      textAlign: "left",
                      fontWeight: "500",
                    }}
                  >
                    {ALERT_TYPE_MAPPING[alertType] || `Alert ${alertType}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lines - Using Badge Style (Original) */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div
              className="text-muted font-weight-bold"
              style={FILTER_STYLES.sectionHeader}
            >
              LINES
            </div>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-primary font-weight-medium"
              style={FILTER_STYLES.toggleButton}
              onClick={handleLinesToggle}
            >
              {selectedLines.length === uniqueLines.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: "8px" }}>
            {uniqueLines.map((line) => (
              <span
                key={line}
                className={`badge cursor-pointer ${
                  selectedLines.includes(line) ? "badge-primary" : "badge-light"
                }`}
                onClick={() =>
                  handleMultiSelect(line, selectedLines, setSelectedLines)
                }
                style={{
                  fontSize: "13px",
                  padding: "6px 12px",
                  textAlign: "left",
                  fontWeight: "500",
                }}
              >
                {line}
              </span>
            ))}
          </div>
        </div>

        {/* Recipes - Using Checkbox Style (Original) */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div
              className="text-muted font-weight-bold"
              style={FILTER_STYLES.sectionHeader}
            >
              RECIPES
            </div>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-primary font-weight-medium"
              style={FILTER_STYLES.toggleButton}
              onClick={handleRecipesToggle}
            >
              {selectedRecipes.length === uniqueRecipes.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
          <input
            type="text"
            className="form-control form-control-sm mb-3"
            placeholder="Search recipes..."
            value={searchFilters.recipes}
            onChange={(e) => updateSearchFilter("recipes", e.target.value)}
            style={FILTER_STYLES.searchInput}
          />
          <div style={FILTER_STYLES.scrollableList}>
            {filteredRecipes.map((recipe) => (
              <div
                key={recipe}
                className="form-check mb-2"
                style={{ textAlign: "left" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedRecipes.includes(recipe)}
                  onChange={() =>
                    handleMultiSelect(
                      recipe,
                      selectedRecipes,
                      setSelectedRecipes
                    )
                  }
                />
                <label
                  className="form-check-label cursor-pointer"
                  style={FILTER_STYLES.formLabel}
                  onClick={() =>
                    handleMultiSelect(
                      recipe,
                      selectedRecipes,
                      setSelectedRecipes
                    )
                  }
                >
                  {recipe}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Objects - Using Checkbox Style (Original) */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div
              className="text-muted font-weight-bold"
              style={FILTER_STYLES.sectionHeader}
            >
              OBJECTS
            </div>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-primary font-weight-medium"
              style={FILTER_STYLES.toggleButton}
              onClick={handleObjectsToggle}
            >
              {selectedObjects.length === uniqueObjects.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
          <input
            type="text"
            className="form-control form-control-sm mb-3"
            placeholder="Search objects..."
            value={searchFilters.objects}
            onChange={(e) => updateSearchFilter("objects", e.target.value)}
            style={FILTER_STYLES.searchInput}
          />
          <div style={FILTER_STYLES.scrollableList}>
            {filteredObjects.map((objectName) => (
              <div
                key={objectName}
                className="form-check mb-2"
                style={{ textAlign: "left" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedObjects.includes(objectName)}
                  onChange={() =>
                    handleMultiSelect(
                      objectName,
                      selectedObjects,
                      setSelectedObjects
                    )
                  }
                />
                <label
                  className="form-check-label cursor-pointer"
                  style={FILTER_STYLES.formLabel}
                  onClick={() =>
                    handleMultiSelect(
                      objectName,
                      selectedObjects,
                      setSelectedObjects
                    )
                  }
                >
                  {objectName}
                </label>
              </div>
            ))}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
